import { BrandIdKeys } from '@seek/brand-types';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import {
  PAGE_ID_PAY_AND_POST,
  PAGE_ID_MULTI_LOCATION,
} from 'src/app/shared/constants/pageUid';
import {
  FEATURE_BRANDING,
  useFeatureToggleData,
} from 'src/app/shared/featureToggle';
import {
  actionDecrementJobCreationInProgressCount,
  actionIncrementJobCreationInProgressCount,
} from 'src/app/v1/state/modules/app/jobCreationInProgressCount';
import { actionUpdateJob } from 'src/app/v1/state/modules/session/job';
import { useCreatePendingJob } from 'src/app/v1/views/hooks/useCreatePendingJob';
import { useGenerateJobIdempotencyKey } from 'src/app/v1/views/hooks/useGenerateJobIdempotencyKey';
import type { AppState } from 'src/types/appState';

export const useJobCreate = () => {
  const {
    advertiserNation,
    brandId,
    draftId,
    interactionId,
    isJobPosted,
    legacyId,
    pageId,
    seekAdvertiserId,
    selectedProductId,
    isPrivateAdvertiser,
  } = useSelector(
    (state: AppState) => ({
      advertiserNation: state.session.hirerAccount.nation,
      brandId: state.formData.brand?.[BrandIdKeys.id],
      draftId: state.session.draftId,
      interactionId: state.session.interactionId,
      isJobPosted: state.session.isJobPosted,
      legacyId: state.formData.brand?.[BrandIdKeys.legacyId],
      pageId: state.session.pageId,
      seekAdvertiserId: state.session.seekAdvertiserId,
      selectedProductId: state.formData.selectedProductId,
      isPrivateAdvertiser: state.session.hirerAccount?.isPrivateAdvertiser,
    }),
    shallowEqual,
  );
  const dispatch = useDispatch();

  const createPendingJob = useCreatePendingJob({
    advertiserNation,
    interactionId,
  });

  const idempotencyKey = useGenerateJobIdempotencyKey();

  const isBrandingEnabled = useFeatureToggleData(FEATURE_BRANDING);

  const createJob = async () => {
    if (
      seekAdvertiserId &&
      draftId &&
      selectedProductId &&
      (pageId === PAGE_ID_PAY_AND_POST || pageId === PAGE_ID_MULTI_LOCATION) &&
      !isJobPosted
    ) {
      const branding =
        isBrandingEnabled && brandId && !isPrivateAdvertiser
          ? { brandId, legacyId }
          : undefined;

      const { data: jobData } = await createPendingJob({
        variables: {
          input: {
            advertiserId: seekAdvertiserId,
            draftId,
            branding,
            idempotencyKey,
          },
        },
      });

      if (jobData?.createJob) {
        dispatch(
          actionUpdateJob({
            id: jobData.createJob.jobId,
            idempotencyKey,
            revisionId: jobData.createJob.revisionId,
          }),
        );
        return {
          jobId: jobData.createJob.jobId,
          revisionId: jobData.createJob.revisionId,
        };
      }
    }
    return null;
  };

  return async () => {
    dispatch(actionIncrementJobCreationInProgressCount());

    const response = await createJob();

    dispatch(actionDecrementJobCreationInProgressCount());

    return response;
  };
};
